import { useState, useMemo } from "react";
import { Modal } from "antd";
import { regionMarketData as staticRegionMarketData } from "./useUserData";
import {
  updateUserScope,
  buildUserScopeData,
  mergeAllModuleUsers,
  getAllMarketCodesFromRegionData,
  messageForScopeSaveError,
} from "../../api/userManagementApi";
import { getRoleDisplayName, normalizeRole } from "../../utils/roleUtils";
import { MODULE_SCOPE_KEYS, emptyProductScopes } from "../../constants/moduleScopes";

/**
 * Custom hook for managing user editing state
 * @param {Array} users - Array of user objects
 * @param {Function} setUsers - Setter for users state
 * @param {Function} getBaseUserKey - Function to extract base user key
 * @param {Object} options - Optional configuration
 * @param {Object} options.regionMarketData - Dynamic region-market data (overrides static data)
 * @param {Function} options.getRegionMarketDataForUser - Function to get region-market data for a specific user
 * @param {Function} options.onSaveSuccess - Callback when save succeeds
 * @param {Function} options.onSaveError - Callback when save fails
 */
export const useUserEditing = (
  users,
  setUsers,
  getBaseUserKey,
  options = {}
) => {
  const {
    regionMarketData: dynamicRegionMarketData,
    getRegionMarketDataForUser,
    onSaveSuccess,
    onSaveError,
  } = options;

  // Use dynamic data if provided, otherwise fall back to static data
  const defaultRegionMarketData = useMemo(() => {
    return dynamicRegionMarketData || staticRegionMarketData;
  }, [dynamicRegionMarketData]);

  // Helper to get region-market data for a specific user (supports per-user workspace)
  const getRegionMarketData = (user) => {
    if (getRegionMarketDataForUser && user) {
      return getRegionMarketDataForUser(user) || defaultRegionMarketData;
    }
    return defaultRegionMarketData;
  };

  const [editingRows, setEditingRows] = useState(new Set());
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  const [selectedMarkets, setSelectedMarkets] = useState({});
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [stagingData, setStagingData] = useState({}); // Edits held here until save

  // Start editing a user row - initialize staging data and enter edit mode
  const startEditUser = (user) => {
    const newEditingRows = new Set(editingRows);
    newEditingRows.add(user.key);

    // Initialize staging data with current user values
    const currentMarkets = Array.isArray(user.markets)
      ? user.markets
      : [user.markets].filter(Boolean);

    setStagingData((prev) => ({
      ...prev,
      [user.key]: {
        role: user.roleName || user.role,
        product: user.productName,
        markets: currentMarkets,
      },
    }));

    setSelectedMarkets((prev) => ({
      ...prev,
      [user.key]: currentMarkets,
    }));

    setEditingRows(newEditingRows);
  };

  // Stop editing a user row - clean up staging data and exit edit mode
  const stopEditUser = (user) => {
    const newEditingRows = new Set(editingRows);
    newEditingRows.delete(user.key);

    // Remove temporary rows when cancelling — check current state so that rows
    // already promoted to permanent by handleSaveUser are not removed.
    if (user.isTemporary) {
      setUsers((prev) => {
        const currentRow = prev.find((u) => u.key === user.key);
        return currentRow?.isTemporary
          ? prev.filter((u) => u.key !== user.key)
          : prev;
      });
    }

    // Clean up row-specific state when stopping edit
    setSelectedMarkets((prev) => {
      const newState = { ...prev };
      delete newState[user.key];
      return newState;
    });
    // Clean up staging data
    setStagingData((prev) => {
      const newState = { ...prev };
      delete newState[user.key];
      return newState;
    });

    setEditingRows(newEditingRows);
  };

  // Legacy function for backward compatibility - toggle edit mode
  const handleEditUser = (user) => {
    if (editingRows.has(user.key)) {
      stopEditUser(user);
    } else {
      startEditUser(user);
    }
  };

  // Record one field edit in staging, leaving the saved user untouched
  const handleStageChange = (user, field, value) => {
    setStagingData((prev) => ({
      ...prev,
      [user.key]: {
        ...prev[user.key],
        [field]: value,
      },
    }));

    // Market edits also feed selectedMarkets, which drives the dropdown display
    if (field === "markets") {
      setSelectedMarkets((prev) => ({
        ...prev,
        [user.key]: value,
      }));
    }
  };

  // State for tracking save operation
  const [isSaving, setIsSaving] = useState(false);

  // Raising someone to the all-market role is the consequential grant on this
  // page, so it gets a deliberate step naming both roles. Admin <-> User edits
  // save directly: they are routine and cheap to reverse.
  const confirmSuperAdminGrant = (fromRole, toRole, name) =>
    new Promise((resolve) => {
      Modal.confirm({
        title: "Grant Super Admin?",
        content: `${name} will change from ${getRoleDisplayName(
          fromRole
        )} to ${getRoleDisplayName(
          toRole
        )}, giving them access to every market and to user management for this workspace.`,
        okText: "Grant Super Admin",
        cancelText: "Cancel",
        centered: true,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleSaveUser = async (user) => {
    const stagedChanges = stagingData[user.key];
    if (!stagedChanges) {
      return;
    }

    // Confirm before the write, not after: cancelling must leave the row staged
    // and unsaved rather than half-applied.
    const stagedRole = normalizeRole(stagedChanges.role);
    if (
      stagedRole === "super_admin" &&
      normalizeRole(user.roleName) !== "super_admin"
    ) {
      const confirmed = await confirmSuperAdminGrant(
        user.roleName,
        stagedChanges.role,
        user.name || user.email || "This user"
      );
      if (!confirmed) return;
    }

    // Role is the single source of truth — propagate it to every row so
    // buildUserScopeData can derive super-admin status from role internally.
    const finalRole = stagedChanges.role || user.roleName;

    const allUserRows = users.filter((u) => u.userId === user.userId);
    const mergedScopes = emptyProductScopes();
    let backendRole = "normal_user";

    allUserRows.forEach((row) => {
      const rowChanges =
        row.key === user.key
          ? stagedChanges
          : { product: row.productName, markets: row.markets, role: finalRole };
      const rowScope = buildUserScopeData(row, rowChanges);
      // Only derive backendRole from the row being edited — other rows may
      // still carry the old role value and would incorrectly overwrite it.
      if (row.key === user.key) {
        backendRole = rowScope.role;
      }
      // Union each of the 5 per-module country lists across the user's rows.
      MODULE_SCOPE_KEYS.forEach((key) => {
        mergedScopes[key] = [
          ...new Set([...mergedScopes[key], ...(rowScope.product_scopes[key] || [])]),
        ];
      });
    });

    const scopeData = {
      is_super_admin: backendRole === "super_admin",
      role: backendRole,
      product_scopes: mergedScopes,
    };

    // Get user ID (from user.userId which comes from API's id_user)
    const idUser = user.userId;

    if (!idUser) {
      console.error("[handleSaveUser] User ID not found:", user);
      onSaveError?.("User ID not found");
      return;
    }

    setIsSaving(true);

    try {
      // Call API to update user scope
      await updateUserScope(idUser, scopeData, user.client);

      // Update the current row with all staged changes (only after API success),
      // then merge rows if the user now has both modules.
      setUsers((prevUsers) => {
        const allAvailableCodes = getAllMarketCodesFromRegionData(defaultRegionMarketData);
        const isSuperAdmin = backendRole === "super_admin";

        // Super admin always collapses to a single "All modules" / "All Markets" row,
        // regardless of how many module rows exist or what was changed in the form.
        if (isSuperAdmin) {
          const baseRow = prevUsers.find((u) => u.userId === user.userId) || user;
          const mergedRow = {
            ...baseRow,
            key: `${user.userId}-all-modules`,
            roleName: stagedChanges.role || user.roleName,
            role: stagedChanges.role || user.role,
            productName: "All modules",
            product: "All modules",
            markets: [...allAvailableCodes],
            productIndex: 0,
            totalProductsForRole: 1,
            isTemporary: false,
          };
          // Replace all rows for this user with the single merged row,
          // preserving the position of the first occurrence.
          const result = [];
          let inserted = false;
          for (const u of prevUsers) {
            if (u.userId === user.userId) {
              if (!inserted) {
                result.push(mergedRow);
                inserted = true;
              }
              // drop remaining rows for this user
            } else {
              result.push(u);
            }
          }
          return result;
        }

        // Normal case: apply staged changes to the edited row, then check for merge.
        const updated = prevUsers.map((u) => {
          if (u.key === user.key) {
            const updatedUser = { ...u };

            if (stagedChanges.role) {
              updatedUser.roleName = stagedChanges.role;
              updatedUser.role = stagedChanges.role;
            }
            if (stagedChanges.product) {
              updatedUser.productName = stagedChanges.product;
            }
            if (stagedChanges.markets) {
              updatedUser.markets = stagedChanges.markets;
            }

            // Promote temp row to permanent so stopEditUser doesn't remove it
            updatedUser.isTemporary = false;

            return updatedUser;
          }
          return u;
        });
        // Collapse to one row only if user now has both modules AND all available markets
        return mergeAllModuleUsers(updated, allAvailableCodes);
      });

      // Clean up staging state after save
      setStagingData((prev) => {
        const newState = { ...prev };
        delete newState[user.key];
        return newState;
      });

      setSelectedMarkets((prev) => {
        const newState = { ...prev };
        const baseUserKey = getBaseUserKey(user.key);
        Object.keys(newState).forEach((key) => {
          if (getBaseUserKey(key) === baseUserKey) {
            delete newState[key];
          }
        });
        return newState;
      });

      onSaveSuccess?.("User settings saved successfully");
    } catch (error) {
      console.error("[handleSaveUser] Failed to save user:", error);
      onSaveError?.(messageForScopeSaveError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarketDropdownOpenChange = (user, open) => {
    const userRegionMarketData = getRegionMarketData(user);
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      const dropdownKey = `market-${user.key}`;
      if (open) {
        newSet.add(dropdownKey);
        // Initialize all regions as expanded when dropdown opens
        setExpandedRegions((prevExpanded) => {
          const newExpanded = new Set(prevExpanded);
          Object.keys(userRegionMarketData).forEach((regionName) => {
            newExpanded.add(`${user.key}-${regionName}`);
          });
          return newExpanded;
        });
      } else {
        newSet.delete(dropdownKey);
        // Don't auto-save - keep staging state for Save button
      }
      return newSet;
    });
  };

  const handleRoleDropdownOpenChange = (user, open) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      const dropdownKey = `role-${user.key}`;
      if (open) {
        newSet.add(dropdownKey);
      } else {
        newSet.delete(dropdownKey);
      }
      return newSet;
    });
  };

  const handleProductDropdownOpenChange = (user, open) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      const dropdownKey = `product-${user.key}`;
      if (open) {
        newSet.add(dropdownKey);
      } else {
        newSet.delete(dropdownKey);
      }
      return newSet;
    });
  };

  const handleMarketSelection = (userKey, region, market) => {
    // Use row-specific userKey directly (no conversion to baseUserKey)
    setSelectedMarkets((prev) => {
      const userMarkets = prev[userKey] || [];
      const isSelected = userMarkets.includes(market);

      let newMarkets;
      if (isSelected) {
        newMarkets = userMarkets.filter((m) => m !== market);
      } else {
        newMarkets = [...userMarkets, market];
      }

      return {
        ...prev,
        [userKey]: newMarkets, // Store under row-specific key
      };
    });
  };

  const handleRegionExpansion = (userKey, region) => {
    // Toggle region expansion only
    const regionKey = `${userKey}-${region}`;
    setExpandedRegions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(regionKey)) {
        newSet.delete(regionKey);
      } else {
        newSet.add(regionKey);
      }
      return newSet;
    });
  };

  const handleRegionSelection = (userKey, region, user = null) => {
    // Use row-specific userKey directly
    // Get region-market data for this user (supports per-user workspace)
    const userRegionMarketData = getRegionMarketData(user);
    const regionData = userRegionMarketData[region];
    if (!regionData) return;

    const regionMarkets = regionData.markets;
    const userMarkets = selectedMarkets[userKey] || [];
    const allRegionMarketsSelected = regionMarkets.every((market) =>
      userMarkets.includes(market)
    );

    setSelectedMarkets((prev) => {
      const currentMarkets = prev[userKey] || [];
      let newMarkets;

      if (allRegionMarketsSelected) {
        // Remove all markets from this region
        newMarkets = currentMarkets.filter(
          (market) => !regionMarkets.includes(market)
        );
      } else {
        // Add all markets from this region
        const marketsToAdd = regionMarkets.filter(
          (market) => !currentMarkets.includes(market)
        );
        newMarkets = [...currentMarkets, ...marketsToAdd];
      }

      return {
        ...prev,
        [userKey]: newMarkets, // Store under row-specific key
      };
    });
  };

  const getDisplayValue = (userKey, user = null) => {
    // Check if we have staging selection for this row
    const stagingMarkets = selectedMarkets[userKey];
    // Get region-market data for this user
    const foundUser = user || users.find((u) => u.key === userKey);
    const userRegionMarketData = getRegionMarketData(foundUser);

    let userMarkets;
    if (stagingMarkets) {
      // Use staging state if available (during editing)
      userMarkets = stagingMarkets;
    } else {
      // Use actual row data when not editing
      if (foundUser && userRegionMarketData[foundUser.market]) {
        userMarkets = userRegionMarketData[foundUser.market].markets;
      } else if (foundUser) {
        userMarkets = [foundUser.market];
      } else {
        userMarkets = [];
      }
    }

    if (userMarkets.length === 0) return "Select markets";

    // Check if any region is fully selected
    for (const [regionName, regionData] of Object.entries(
      userRegionMarketData
    )) {
      const regionMarkets = regionData.markets;
      if (regionMarkets.every((market) => userMarkets.includes(market))) {
        // If only one region is fully selected and no other markets
        const otherMarkets = userMarkets.filter(
          (market) => !regionMarkets.includes(market)
        );
        if (
          otherMarkets.length === 0 &&
          userMarkets.length === regionMarkets.length
        ) {
          return regionName;
        }
      }
    }

    // Otherwise show individual markets or count
    if (userMarkets.length === 1) {
      return userMarkets[0];
    } else {
      return `${userMarkets.length} markets selected`;
    }
  };

  const isRegionFullySelected = (userKey, region, user = null) => {
    // Use row-specific userKey directly
    const userMarkets = selectedMarkets[userKey] || [];
    const userRegionMarketData = getRegionMarketData(user);
    const regionData = userRegionMarketData[region];
    if (!regionData) return false;
    return regionData.markets.every((market) => userMarkets.includes(market));
  };

  const isMarketSelected = (userKey, market) => {
    // Use row-specific userKey directly
    const userMarkets = selectedMarkets[userKey] || [];
    return userMarkets.includes(market);
  };

  return {
    editingRows,
    setEditingRows,
    openDropdowns,
    setOpenDropdowns,
    selectedMarkets,
    setSelectedMarkets,
    expandedRegions,
    setExpandedRegions,
    stagingData,
    setStagingData,
    startEditUser,
    stopEditUser,
    handleEditUser,
    handleSaveUser,
    handleStageChange,
    handleMarketDropdownOpenChange,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleMarketSelection,
    handleRegionExpansion,
    handleRegionSelection,
    getDisplayValue,
    isRegionFullySelected,
    isMarketSelected,
    // Expose region-market data helpers
    getRegionMarketData,
    defaultRegionMarketData,
    // Save operation state
    isSaving,
  };
};
